<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { PopupBasicHeader, Element, colors } from 'wepin-vue-component'
import { useI18nTypes } from '@/composables/useI18nTypes'
import { useSettingsStore } from '@/stores'
import { useTransactionStore } from '@/stores/transaction'
import { useWalletStore } from '@/stores/wallet'
import { Account, Transaction, type BaseAccount } from '@/types'

type ISortBy = 'name' | 'balance' | 'recent'

const { t } = useI18nTypes()

const emits = defineEmits<{
  (event: 'close'): void
}>()
const walletStore = useWalletStore()
const settingStore = useSettingsStore()
const transactionStore = useTransactionStore()

const accountList = computed<BaseAccount[]>(() => {
  if (
    !walletStore.groupAccountList.length ||
    !walletStore.fullAccountList.length
  )
    return []
  if (settingStore.accountLayout.group) {
    return walletStore.groupAccountList
  } else {
    return walletStore.fullAccountList
  }
})
onMounted(async () => {
  await Promise.all(
    accountList.value.map((account) => {
      return account.fetchTransactionList()
    }),
  )
})

const matchingTxList = computed(() => {
  const mapper: { [key: string]: string[] } = {}
  transactionStore.accountTransactionList.forEach((tx) => {
    const id = 'account' + tx.accountId
    if (mapper[id]) mapper[id].push(tx.createDate)
    else mapper[id] = [tx.createDate]
  })
  if (!settingStore.accountLayout.group) {
    transactionStore.accountTokenTransactionList.forEach((tx) => {
      if (!(tx instanceof Transaction)) return
      if (!(tx.account instanceof Account)) return
      const id =
        'accountToken' +
        tx.account?.tokens?.find(
          (token) => token.tokenId === parseInt(tx.accountTokenId as string),
        )?.id
      if (mapper[id]) mapper[id].push(tx.createDate)
      else mapper[id] = [tx.createDate]
    })
  }
  Object.keys(mapper).forEach((key) => {
    mapper[key].sort((dateA, dateB) => {
      return dateB > dateA ? 1 : dateB < dateA ? -1 : 0
    })
    mapper[key] = [mapper[key][0]]
  })
  accountList.value.forEach((account: BaseAccount) => {
    const id = account.getKey()
    if (mapper[id]) return
    mapper[id] = ['0']
  })

  return mapper
})
const selected = ref<ISortBy>()
const sortList: { id: ISortBy; text: string }[] = [
  {
    id: 'name',
    text: t('sort_account.name'),
  },
  {
    id: 'balance',
    text: t('sort_account.balance'),
  },
  {
    id: 'recent',
    text: t('sort_account.recent'),
  },
]
const onClick = (id: ISortBy) => {
  selected.value = id
  walletStore.sortAccountList(id, matchingTxList.value)
}
</script>

<template>
  <div class="sort-account">
    <PopupBasicHeader
      :title="t('sort_account.title')"
      :exit="() => emits('close')"
    />
    <ul class="sort-list">
      <li
        v-for="item in sortList"
        :key="item.id"
        class="sort-item"
        :class="selected === item.id ? 'selected' : ''"
        @click="onClick(item.id)"
      >
        {{ item.text }}
        <Element
          v-show="selected === item.id"
          name="Check"
          :color="colors.currentThemeRef.value.main"
        />
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
@import 'wepin-vue-component/src/styles/typeface.module.scss';

.sort-account {
  display: flex;
  flex-direction: column;
}
.sort-list {
  padding: 0;
  margin: 0;
  padding: {
    top: 10px;
    bottom: 20px;
  }
  & > .sort-item {
    display: flex;
    justify-content: space-between;
    align-items: center;
    @include typeface('Paragraph_16_r');
    color: var(--grey_111);
    padding: {
      top: 17px;
      left: 30px;
      bottom: 17px;
      right: 24.22px;
    }
  }
  & .sort-item:hover {
    background-color: var(--bg_sub);
  }
}
</style>
